// Ported from obsidian-plugin/src/components/ChatInput.tsx.
// Obsidian adaptations:
//   - Removed [[wikilink]] autocomplete + vault reference chips.
//     No vault in the browser; users just type plain text.
//   - Removed RagOptionsPopup (plugin settings UI) — replaced with a small
//     inline popover that edits ChatOptions kept in local state. It is the
//     only place search options are set; /settings holds the LLM config.
//   - Removed Graph / Timeline panel toggles (deferred for v2).
//   - Removed `useApp()` hook usage.
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  KeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import type { ChatOptions, ChatMode, RetrievalProfile } from '../../types';
import { DEFAULT_CHAT_OPTIONS } from '../../types';
import Stepper from '../../components/Stepper';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled: boolean;
  streaming?: boolean;
  onStop?: () => void;
  useRag: boolean;
  onToggleRag: () => void;
  chatOptions: ChatOptions;
  onChatOptionsChange: (partial: Partial<ChatOptions>) => void;
  estimatedTokens?: number;
  maxContextTokens?: number;
  graphOpen?: boolean;
  onToggleGraph?: () => void;
  hasUnseenGraphData?: boolean;
  subgraphScope?: 'none' | 'search_within' | 'expand_from';
  pinnedNodeCount?: number;
  llmStatus?: string;
}

function formatTokens(n: number): string {
  if (n >= 1000) return `~${(n / 1000).toFixed(1)}k`;
  return `~${n}`;
}

function ContextRing({ tokens, maxTokens }: { tokens: number; maxTokens: number }) {
  const pct = Math.min(tokens / maxTokens, 1);
  const r = 9;
  const cx = 11;
  const cy = 11;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - pct);

  const strokeColor =
    pct < 0.5
      ? 'var(--color-green)'
      : pct < 0.75
        ? 'var(--color-yellow)'
        : pct < 0.9
          ? 'var(--color-orange)'
          : 'var(--color-red)';

  const tooltipText = `${formatTokens(tokens)} / ${formatTokens(maxTokens)} tokens (${Math.round(pct * 100)}%)`;

  return (
    <div className="malpedia-context-ring-wrap">
      <svg
        className="malpedia-context-ring"
        width="22"
        height="22"
        viewBox="0 0 22 22"
        aria-label={tooltipText}
      >
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          strokeWidth="3"
          className="malpedia-context-ring-track"
        />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={strokeColor}
          strokeWidth="3"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${cx} ${cy})`}
        />
      </svg>
      <div className="malpedia-context-ring-tooltip">{tooltipText}</div>
    </div>
  );
}

export interface ChatInputHandle {
  setValue: (text: string) => void;
  focus: () => void;
}

// ── User-friendly naming maps ──────────────────────────────────────────

// 'llm_only' is not offered here — the RAG / Chat pill covers it.
type SearchMode = Exclude<ChatMode, 'llm_only'>;

const MODES: { value: SearchMode; label: string; description: string }[] = [
  { value: 'auto', label: 'Auto', description: 'Analyzes your question and picks the best approach' },
  { value: 'vector', label: 'Semantic', description: 'Finds passages by meaning similarity and keywords' },
  { value: 'planned', label: 'Planned', description: 'A planner combines several search approaches' },
];

type DepthPreset = 'auto' | 'quick' | 'balanced' | 'thorough' | 'deep' | 'custom';

const DEPTHS: { value: DepthPreset; label: string; api: RetrievalProfile; description: string }[] = [
  { value: 'auto', label: 'Auto', api: 'auto', description: 'Decided per question, based on its complexity' },
  { value: 'quick', label: 'Quick', api: 'narrow', description: '~4 passages — fast lookups, specific entities' },
  { value: 'balanced', label: 'Balanced', api: 'normal', description: '~8 passages — good for most questions' },
  { value: 'thorough', label: 'Thorough', api: 'broad', description: '~20 passages — complex topics, comparisons' },
  { value: 'deep', label: 'Deep', api: 'exhaustive', description: '~50 passages — research, campaign analysis' },
  { value: 'custom', label: 'Custom', api: 'manual', description: 'Set every parameter yourself' },
];

const PROFILE_VALUES: Record<string, { topK: number; topSources: number; fusionCap: number; perReport: number }> = {
  quick:    { topK: 5,  topSources: 4,  fusionCap: 10,  perReport: 5 },
  balanced: { topK: 10, topSources: 8,  fusionCap: 20,  perReport: 5 },
  thorough: { topK: 25, topSources: 20, fusionCap: 50,  perReport: 3 },
  deep:     { topK: 50, topSources: 50, fusionCap: 150, perReport: 3 },
};

type ContextMode = 'passages' | 'neighbors' | 'full';

function contextModeOf(o: ChatOptions): ContextMode {
  if (o.fullDocContext) return 'full';
  if (o.neighborExpansion) return 'neighbors';
  return 'passages';
}

/** Search options that differ from the defaults (drives the button dot). */
function changedOptionCount(o: ChatOptions): number {
  const d = DEFAULT_CHAT_OPTIONS;
  return [
    o.mode !== d.mode && o.mode !== 'llm_only',
    o.retrievalProfile !== d.retrievalProfile,
    o.iocPivot !== d.iocPivot,
    o.webSearch !== d.webSearch,
    contextModeOf(o) !== 'passages',
    o.agentLoop !== d.agentLoop,
    o.contextLayout !== d.contextLayout,
  ].filter(Boolean).length;
}

function hasAdvancedChanges(o: ChatOptions): boolean {
  return (
    contextModeOf(o) !== 'passages' ||
    o.agentLoop !== 'default' ||
    o.contextLayout !== 'default' ||
    o.maxContextTokens > 0
  );
}

/** Inline toggle switch — hidden checkbox + styled track/thumb. */
function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="malpedia-rag-toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="malpedia-rag-toggle-track" />
    </label>
  );
}

/** Row of mutually exclusive chips. */
function Segmented<T extends string>({
  value,
  options,
  onChange,
  columns,
  label,
}: {
  value: T;
  options: { value: T; label: string; description?: string }[];
  onChange: (v: T) => void;
  columns?: number;
  label: string;
}) {
  return (
    <div
      className="malpedia-rag-segmented"
      role="radiogroup"
      aria-label={label}
      style={{ gridTemplateColumns: `repeat(${columns ?? options.length}, 1fr)` }}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={`malpedia-rag-segment${value === o.value ? ' active' : ''}`}
          title={o.description}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function OptionRow({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <div className="malpedia-rag-option-row">
      <span className="malpedia-rag-option-text">
        <span>{label}</span>
        {help && <span className="malpedia-rag-option-help">{help}</span>}
      </span>
      {children}
    </div>
  );
}

/** Per-question search options, saved in this browser. */
function OptionsPopover({
  options,
  onChange,
  onClose,
}: {
  options: ChatOptions;
  onChange: (partial: Partial<ChatOptions>) => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [advanced, setAdvanced] = useState(() => hasAdvancedChanges(options));
  const popupRef = useRef<HTMLDivElement>(null);

  // Close on click outside (parent wrap includes the trigger button)
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const wrap = popupRef.current?.parentElement;
      if (wrap && !wrap.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  useEffect(() => {
    popupRef.current?.focus();
  }, []);

  const mode: SearchMode = options.mode === 'llm_only' ? 'auto' : options.mode;
  const depth = DEPTHS.find((d) => d.api === options.retrievalProfile) ?? DEPTHS[0];
  const profileVals = PROFILE_VALUES[depth.value];
  const changed = changedOptionCount(options);

  const resetAll = () => {
    const d = DEFAULT_CHAT_OPTIONS;
    onChange({
      mode: d.mode,
      retrievalProfile: d.retrievalProfile,
      topK: d.topK,
      customTopSources: d.customTopSources,
      customFusionCap: d.customFusionCap,
      customMaxChunksPerDoc: d.customMaxChunksPerDoc,
      iocPivot: d.iocPivot,
      webSearch: d.webSearch,
      neighborExpansion: d.neighborExpansion,
      fullDocContext: d.fullDocContext,
      agentLoop: d.agentLoop,
      contextLayout: d.contextLayout,
    });
  };

  return (
    <div
      className="malpedia-rag-options-popup"
      ref={popupRef}
      role="dialog"
      aria-label="Search options"
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
      tabIndex={-1}
    >
      <div className="malpedia-rag-popup-head">
        <span className="malpedia-rag-popup-heading">Search options</span>
        {changed > 0 && (
          <button type="button" className="malpedia-rag-footer-link" onClick={resetAll} title="Back to the defaults">
            Reset
          </button>
        )}
      </div>

      {/* Strategy */}
      <div className="malpedia-rag-section-label">Strategy</div>
      <Segmented
        label="Strategy"
        value={mode}
        options={MODES}
        onChange={(m) => onChange({ mode: m })}
      />
      <div className="malpedia-rag-hint">{MODES.find((m) => m.value === mode)?.description}</div>

      {/* Depth */}
      <div className="malpedia-rag-section-label">Depth</div>
      <Segmented
        label="Depth"
        value={depth.value}
        options={DEPTHS}
        columns={3}
        onChange={(d) => onChange({ retrievalProfile: DEPTHS.find((x) => x.value === d)!.api })}
      />
      <div className="malpedia-rag-hint">
        {profileVals
          ? `${depth.description} · ${profileVals.topK} candidates, ${profileVals.topSources} in context`
          : depth.description}
      </div>

      {depth.value === 'custom' && (
        <div className="malpedia-rag-subgroup">
          <OptionRow label="Candidates" help="Passages retrieved per search">
            <Stepper value={options.topK} onChange={(v) => onChange({ topK: v })} min={1} max={200} step={5} />
          </OptionRow>
          <OptionRow label="Context size" help="Passages given to the model">
            <Stepper value={options.customTopSources} onChange={(v) => onChange({ customTopSources: v })} min={1} max={100} step={5} />
          </OptionRow>
          <OptionRow label="Merge limit" help="Results kept when merging searches">
            <Stepper value={options.customFusionCap} onChange={(v) => onChange({ customFusionCap: v })} min={5} max={500} step={10} />
          </OptionRow>
          <OptionRow label="Per report" help="Max passages from one report (0 = no limit)">
            <Stepper value={options.customMaxChunksPerDoc} onChange={(v) => onChange({ customMaxChunksPerDoc: v })} min={0} max={10} step={1} />
          </OptionRow>
        </div>
      )}

      <div className="malpedia-rag-separator" />

      {/* Sources */}
      <OptionRow label="IOC search" help="Look up hashes, domains and IPs in your question">
        <ToggleSwitch checked={options.iocPivot} onChange={(v) => onChange({ iocPivot: v })} />
      </OptionRow>
      <OptionRow label="Web search" help="Also search the public web">
        <ToggleSwitch checked={options.webSearch} onChange={(v) => onChange({ webSearch: v })} />
      </OptionRow>

      {advanced && (
        <>
          <div className="malpedia-rag-separator" />
          <div className="malpedia-rag-section-label">Context per hit</div>
          <Segmented<ContextMode>
            label="Context per hit"
            value={contextModeOf(options)}
            options={[
              { value: 'passages', label: 'Passage', description: 'Only the matching passage' },
              { value: 'neighbors', label: '+ Neighbours', description: 'Add the passages around each match' },
              { value: 'full', label: 'Full report', description: 'Give the model the whole report' },
            ]}
            onChange={(c) => onChange({ neighborExpansion: c === 'neighbors', fullDocContext: c === 'full' })}
          />

          <div className="malpedia-rag-section-label">
            Experimental <span className="malpedia-rag-section-aside">· “Server” = site default</span>
          </div>
          <OptionRow label="Agent loop" help="Model picks its own search tools step by step">
            <Segmented
              label="Agent loop"
              value={options.agentLoop}
              options={[
                { value: 'default', label: 'Server' },
                { value: 'on', label: 'On' },
                { value: 'off', label: 'Off' },
              ]}
              onChange={(v) => onChange({ agentLoop: v })}
            />
          </OptionRow>
          <OptionRow label="Context layout" help="Tiered: top reports in full, the rest as summaries">
            <Segmented
              label="Context layout"
              value={options.contextLayout}
              options={[
                { value: 'default', label: 'Server' },
                { value: 'flat', label: 'Flat' },
                { value: 'tiered', label: 'Tiered' },
              ]}
              onChange={(v) => onChange({ contextLayout: v })}
            />
          </OptionRow>

          <div className="malpedia-rag-section-label">Display</div>
          <OptionRow label="Token budget" help="Shows a fill ring by the input; 0 = plain counter">
            <Stepper
              value={options.maxContextTokens}
              onChange={(v) => onChange({ maxContextTokens: v })}
              min={0}
              max={200_000}
              step={4000}
            />
          </OptionRow>
        </>
      )}

      <div className="malpedia-rag-separator" />

      <div className="malpedia-rag-footer">
        <button
          type="button"
          className="malpedia-rag-footer-link"
          onClick={() => setAdvanced((v) => !v)}
          aria-expanded={advanced}
        >
          {advanced ? 'Fewer options' : 'More options'}
        </button>
        <button
          type="button"
          className="malpedia-rag-footer-link accent"
          onClick={() => { onClose(); navigate('/settings'); }}
        >
          Model &amp; API keys →
        </button>
      </div>
      <div className="malpedia-rag-popup-note">Saved in this browser · applies to your next question</div>
    </div>
  );
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(function ChatInput(
  {
    onSend,
    disabled,
    streaming,
    onStop,
    useRag,
    onToggleRag,
    chatOptions,
    onChatOptionsChange,
    estimatedTokens,
    maxContextTokens,
    graphOpen,
    onToggleGraph,
    hasUnseenGraphData,
    subgraphScope,
    pinnedNodeCount,
    llmStatus,
  },
  ref,
) {
  const navigate = useNavigate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const llmBlocked = !!llmStatus && llmStatus !== 'ok';
  const llmBannerText =
    llmStatus === 'not_configured'
      ? 'No LLM backend configured.'
      : llmBlocked
        ? `LLM unavailable: ${llmStatus!.replace(/^unreachable:\s*/, '')}`
        : '';

  useImperativeHandle(
    ref,
    () => ({
      setValue: (text: string) => {
        setInputValue(text);
        requestAnimationFrame(() => textareaRef.current?.focus());
      },
      focus: () => textareaRef.current?.focus(),
    }),
    [],
  );

  const handleSend = useCallback(() => {
    const value = inputValue.trim();
    if (!value || disabled) return;
    onSend(value);
    setInputValue('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  }, [inputValue, disabled, onSend]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    handleInput();
  };

  return (
    <div className="malpedia-input-area">
      <div className="malpedia-input-card">
        {llmBlocked && (
          <div
            className="malpedia-input-llm-warning"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              marginBottom: '0.5rem',
              borderRadius: '0.5rem',
              backgroundColor: 'var(--background-modifier-warning, #fff3cd)',
              color: 'var(--text-warning, #856404)',
              fontSize: '0.85rem',
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>&#x26A0;</span>
            <span style={{ flex: 1 }}>{llmBannerText}</span>
            <button
              type="button"
              className="malpedia-rag-footer-link accent"
              onClick={() => navigate('/settings')}
            >
              Go to Settings →
            </button>
          </div>
        )}
        <textarea
          ref={textareaRef}
          className="malpedia-chat-input"
          placeholder={
            llmBlocked
              ? 'LLM not configured — see Settings'
              : 'Ask about threat actors, malware families… (Enter to send, Shift+Enter for newline)'
          }
          value={inputValue}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          disabled={llmBlocked}
          rows={1}
        />
        <div className="malpedia-input-toolbar">
          <div className="malpedia-input-toolbar-left">
            <div className="malpedia-input-toggle-pill">
              <button
                className={`malpedia-input-toggle-btn${useRag ? ' active' : ''}`}
                onClick={useRag ? undefined : onToggleRag}
                title="Search threat reports"
              >
                RAG
              </button>
              <button
                className={`malpedia-input-toggle-btn${!useRag ? ' active' : ''}`}
                onClick={useRag ? onToggleRag : undefined}
                title="Plain conversation"
              >
                Chat
              </button>
            </div>
            {useRag && (
              <div className="malpedia-input-options-wrap">
                <button
                  className={`malpedia-input-options-btn${showPopup ? ' active' : ''}`}
                  onClick={() => setShowPopup((v) => !v)}
                  title="Search options"
                  aria-label="Search options"
                  aria-expanded={showPopup}
                >
                  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <line x1="2" y1="4" x2="14" y2="4" />
                    <line x1="2" y1="12" x2="14" y2="12" />
                    <circle cx="10" cy="4" r="1.8" fill="currentColor" />
                    <circle cx="6" cy="12" r="1.8" fill="currentColor" />
                  </svg>
                  {changedOptionCount(chatOptions) > 0 && (
                    <span className="malpedia-input-options-dot" aria-hidden="true" />
                  )}
                </button>
                {showPopup && (
                  <OptionsPopover
                    options={chatOptions}
                    onChange={onChatOptionsChange}
                    onClose={() => setShowPopup(false)}
                  />
                )}
              </div>
            )}
            {onToggleGraph && (
              <button
                className={`malpedia-input-graph-toggle${graphOpen ? ' active' : ''}`}
                onClick={onToggleGraph}
                title={graphOpen ? 'Close graph view' : 'Open graph view'}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <circle cx="4" cy="4" r="2" />
                  <circle cx="12" cy="4" r="2" />
                  <circle cx="8" cy="12" r="2" />
                  <line x1="5.5" y1="5.5" x2="7" y2="10.5" />
                  <line x1="10.5" y1="5.5" x2="9" y2="10.5" />
                </svg>
                {hasUnseenGraphData && !graphOpen && (
                  <span className="malpedia-input-graph-toggle-badge" aria-hidden="true" />
                )}
              </button>
            )}
            {subgraphScope !== 'none' && (pinnedNodeCount ?? 0) > 0 && (
              <span className="malpedia-scope-badge">
                Scoped to {pinnedNodeCount} node{pinnedNodeCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          {estimatedTokens !== undefined &&
            estimatedTokens > 0 &&
            (maxContextTokens && maxContextTokens > 0 ? (
              <ContextRing tokens={estimatedTokens} maxTokens={maxContextTokens} />
            ) : (
              <span
                className={`malpedia-token-counter${estimatedTokens > 8000 ? ' warn' : ''}`}
                title="Estimated context tokens (1 token ≈ 4 chars)"
              >
                {formatTokens(estimatedTokens)} tokens
              </span>
            ))}
          {streaming && onStop ? (
            <button className="malpedia-stop-btn" onClick={onStop}>
              ■ Stop
            </button>
          ) : (
            <button
              className="malpedia-send-btn"
              onClick={handleSend}
              disabled={disabled}
              title="Send message"
            >
              Send
            </button>
          )}
        </div>
      </div>
    </div>
  );
});
